import { Head, Link } from "@inertiajs/react";
import { salonpageR2ImageUrl, salonpageR2LogoUrl, salonpageVideoMp4Url, salonpageVideoPosterUrl, encodeSalonpagePathId } from "@/lib/_url";
import { useVideoPlayer } from "@/lib/_use-video-player";
import { useMemo, useState, useEffect, useRef } from "react";

const PER_PAGE = 30;

interface SalonListItem {
  id: string;
  hpbSalonId: string;
  name: string;
  catchCopy: string;
  logoImageId?: string | null;
  primaryImageId?: string | null;
  heroVideoId?: string | null;
  heroVideoOrientation?: string | null;
  featureToggles?: {
    pagePublished?: boolean | number | null;
  };
}

interface Summary {
  totalSalons: number;
  totalMenus: number;
  totalStaff: number;
  totalStyles: number;
}

interface Meta {
  title: string;
  description: string;
}

interface Props {
  summary?: Summary;
  meta?: Meta;
  appStage?: "local" | "dev" | "main";
}

const EMPTY_SUMMARY: Summary = {
  totalSalons: 0,
  totalMenus: 0,
  totalStaff: 0,
  totalStyles: 0,
};

export default function SalonpageIndex({ summary = EMPTY_SUMMARY, meta, appStage = "main" }: Props) {
  const [salons, setSalons] = useState<SalonListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [showPublished, setShowPublished] = useState(true);
  const [showUnpublished, setShowUnpublished] = useState(true);
  const showDraftControls = appStage !== "main";
  const normalizedQuery = query.trim().toLowerCase();
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const res = await fetch("/salons.ndjson", { signal: controller.signal });
        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop()!;
          for (const line of lines) {
            if (line.trim()) {
              setSalons((prev) => [...prev, JSON.parse(line)]);
            }
          }
        }
      } catch (e) {
        if ((e as Error).name !== "AbortError") throw e;
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  const filteredSalons = useMemo(() => {
    return salons.filter((salon) => {
      const isPublished = flagEnabled(salon.featureToggles?.pagePublished);
      if (showDraftControls) {
        if (isPublished && !showPublished) return false;
        if (!isPublished && !showUnpublished) return false;
      }
      if (!normalizedQuery) return true;
      const haystack = [salon.name, salon.hpbSalonId].join(" ").toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [salons, normalizedQuery, showPublished, showUnpublished, showDraftControls]);

  const totalPages = Math.ceil(filteredSalons.length / PER_PAGE);
  const pagedSalons = filteredSalons.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => {
    setPage(1);
  }, [normalizedQuery, showPublished, showUnpublished]);

  return (
    <>
      <Head>
        <title>{meta?.title ?? "Salon Pages | Hairbook"}</title>
        {meta?.description && <meta name="description" content={meta.description} />}
      </Head>

      <div className="min-h-screen bg-[#f6f6f3] text-zinc-950">
        <header className="sticky top-0 z-20 border-b border-zinc-200 bg-white/90 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
            <Link href="/salons" className="min-w-0">
              <p className="text-base font-black tracking-tight">Salon Pages</p>
              <p className="text-[11px] font-bold text-zinc-400">Hairbook</p>
            </Link>
            <nav className="flex items-center gap-4 text-xs font-bold text-zinc-500">
              <Link href="/staffs" className="hover:text-zinc-950">スタッフ</Link>
              <a href="https://hairbook.jp/" className="hidden hover:text-zinc-950 sm:inline">hairbook.jp</a>
            </nav>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-4 py-5 sm:py-8">
          <section className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <h1 className="text-2xl font-black tracking-tight sm:text-3xl">サロン一覧</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">
                取り込み済みのサロンページを確認できます。
              </p>
            </div>

            <div className="grid grid-cols-4 gap-2 rounded-lg border border-zinc-200 bg-white p-2 shadow-sm">
              <SummaryItem label="salons" value={summary.totalSalons} />
              <SummaryItem label="menus" value={summary.totalMenus} />
              <SummaryItem label="staff" value={summary.totalStaff} />
              <SummaryItem label="styles" value={summary.totalStyles} />
            </div>
          </section>

          <section className="mt-5 rounded-lg border border-zinc-200 bg-white p-3 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="min-w-0 flex-1">
                <span className="sr-only">サロン検索</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="サロン名、HPB ID で検索"
                  className="h-11 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm outline-none focus:border-zinc-900"
                />
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {showDraftControls && (
                  <>
                    <StatusToggle label="公開済み" checked={showPublished} onChange={setShowPublished} />
                    <StatusToggle label="未公開" checked={showUnpublished} onChange={setShowUnpublished} />
                  </>
                )}
                {!loading && (
                  <p className="ml-auto text-sm font-bold text-zinc-500 sm:ml-2">
                    <span className="tabular-nums text-zinc-950">{filteredSalons.length}</span> / {salons.length} 件
                  </p>
                )}
              </div>
            </div>
          </section>

          {pagedSalons.length > 0 ? (
            <>
              <section className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {pagedSalons.map((salon) => (
                  <SalonCard key={salon.id} salon={salon} showPublicationStatus={showDraftControls} />
                ))}
              </section>
              {totalPages > 1 && (
                <nav className="mt-6 flex items-center justify-center gap-1">
                  <PageButton disabled={page <= 1} onClick={() => setPage(1)}>«</PageButton>
                  <PageButton disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</PageButton>
                  {paginationRange(page, totalPages).map((n) =>
                    n === "..." ? (
                      <span key={n} className="px-2 text-sm text-zinc-400">…</span>
                    ) : (
                      <PageButton key={n} active={n === page} onClick={() => setPage(n as number)}>{n}</PageButton>
                    )
                  )}
                  <PageButton disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</PageButton>
                  <PageButton disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</PageButton>
                </nav>
              )}
            </>
          ) : loading ? (
            <section className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <SalonCardSkeleton key={i} />
              ))}
            </section>
          ) : (
            <section className="mt-4 grid min-h-48 place-items-center rounded-lg border border-dashed border-zinc-300 bg-white">
              <p className="text-sm font-bold text-zinc-400">該当するサロンがありません。</p>
            </section>
          )}
        </main>
      </div>
    </>
  );
}

function SummaryItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-[4.5rem] rounded-md bg-zinc-50 px-3 py-2 text-center">
      <p className="text-lg font-black tabular-nums">{value.toLocaleString()}</p>
      <p className="mt-0.5 text-[10px] font-bold uppercase text-zinc-400">{label}</p>
    </div>
  );
}

function StatusToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className={`inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border px-2.5 text-xs font-black transition ${checked ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300 bg-white text-zinc-500"}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="sr-only"
      />
      <span className={`h-2 w-2 rounded-full ${checked ? "bg-emerald-300" : "bg-zinc-300"}`} />
      {label}
    </label>
  );
}

function SalonCard({ salon, showPublicationStatus }: { salon: SalonListItem; showPublicationStatus: boolean }) {
  const imageUrl = salon.primaryImageId
    ? salonpageR2ImageUrl(salon.id, salon.primaryImageId, "gallery")
    : salon.logoImageId
      ? salonpageR2LogoUrl(salon.id, "thumbnail")
      : "";
  const logoUrl = salon.logoImageId ? salonpageR2LogoUrl(salon.id, "thumbnail") : "";
  const href = `/salons/${encodeSalonpagePathId(salon.id)}`;
  const isPublished = flagEnabled(salon.featureToggles?.pagePublished);
  const statusLabel = isPublished ? "公開ON" : "公開OFF";

  return (
    <Link
      href={href}
      className="group block overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
    >
      <div className="relative aspect-[16/11] overflow-hidden bg-zinc-950">
        <SalonCardMedia
          alt={salon.name}
          imageUrl={imageUrl}
          videoId={salon.heroVideoId}
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/78 via-black/12 to-transparent" />
        {showPublicationStatus && (
          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/50 px-2 py-1 text-[10px] font-black text-white backdrop-blur">
            <span className={`h-1.5 w-1.5 rounded-full ${isPublished ? "bg-emerald-400" : "bg-rose-400"}`} />
            {statusLabel}
          </div>
        )}
        <div className="absolute right-3 top-3 rounded-full bg-white/85 px-2 py-1 text-[10px] font-black text-zinc-800 shadow-sm backdrop-blur">
          {salon.heroVideoId ? "動画ON" : "動画OFF"}
        </div>
        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
          <div className="mb-2 flex items-end gap-2">
            {logoUrl && <img src={logoUrl} alt="" className="h-9 w-9 rounded-md border border-white/20 bg-white object-contain p-1" loading="lazy" />}
            <p className="text-[10px] font-bold text-white/70">{salon.hpbSalonId}</p>
          </div>
          <h2 className="line-clamp-2 text-base font-black leading-5 tracking-tight">{salon.name}</h2>
        </div>
      </div>

      <div className="space-y-3 p-3">
        {salon.catchCopy && <p className="line-clamp-2 text-xs leading-5 text-zinc-600">{salon.catchCopy}</p>}
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 gap-1.5">
            {salonKeywords(salon).map((keyword) => (
              <span key={keyword} className="truncate rounded-full bg-zinc-100 px-2 py-1 text-[10px] font-bold text-zinc-500">
                {keyword}
              </span>
            ))}
          </div>
          <span className="shrink-0 text-xs font-black text-zinc-950">詳細を見る</span>
        </div>
      </div>
    </Link>
  );
}

function flagEnabled(value?: boolean | number | null) {
  return value === true || value === 1;
}

function SalonCardMedia({ alt, imageUrl, videoId }: { alt: string; imageUrl: string; videoId?: string | null }) {
  const [hasError, setHasError] = useState(false);
  const videoUrl = videoId ? salonpageVideoMp4Url(videoId) : null;
  const posterUrl = videoId ? salonpageVideoPosterUrl(videoId) : imageUrl;
  const { videoRef, isLoading } = useVideoPlayer({
    src: hasError ? null : videoUrl,
    poster: posterUrl,
    muted: true,
    loop: true,
  });

  if (!videoUrl || hasError) {
    return <img src={imageUrl} alt={alt} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" loading="lazy" />;
  }

  return (
    <>
      <video
        ref={videoRef}
        aria-label={alt}
        playsInline
        muted
        loop
        preload="metadata"
        poster={posterUrl}
        onError={() => setHasError(true)}
        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
      />
      <div
        aria-hidden="true"
        className={[
          "pointer-events-none absolute inset-0 transition-opacity duration-500",
          isLoading ? "opacity-100" : "opacity-0",
        ].join(" ")}
      >
        <div className="absolute inset-0 bg-zinc-950" />
        <div className="absolute inset-0 animate-shimmer bg-[linear-gradient(105deg,transparent_40%,rgba(255,255,255,0.08)_50%,transparent_60%)] bg-[length:200%_100%]" />
      </div>
    </>
  );
}

function salonKeywords(salon: SalonListItem) {
  const source = `${salon.name} ${salon.catchCopy}`;
  const keywords = [
    ["派手髪", /派手|カラー|色|ブリーチ|ホワイト/],
    ["エクステ", /エクステ/],
    ["メンズ", /メンズ|男性/],
    ["駅近", /駅|徒歩/],
  ] as const;
  const matched = keywords.filter(([, pattern]) => pattern.test(source)).map(([label]) => label);
  return (matched.length > 0 ? matched : ["サロンLP"]).slice(0, 2);
}

function SalonCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
      <div className="flex items-center gap-3 p-3">
        <div className="h-16 w-16 animate-pulse rounded-md bg-zinc-100" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-3 w-16 animate-pulse rounded bg-zinc-100" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-zinc-100" />
          <div className="h-3 w-full animate-pulse rounded bg-zinc-50" />
        </div>
      </div>
    </div>
  );
}

function PageButton({ children, active, disabled, onClick }: { children: React.ReactNode; active?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border px-2 text-sm font-bold transition ${
        active
          ? "border-zinc-900 bg-zinc-950 text-white"
          : disabled
            ? "cursor-not-allowed border-zinc-200 text-zinc-300"
            : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
      }`}
    >
      {children}
    </button>
  );
}

function paginationRange(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  if (current > 3) pages.push("...");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push("...");
  if (total > 1) pages.push(total);
  return pages;
}
